import React, { useState } from 'react';
import {
  Button,
  Input,
  Progress,
  Stack,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import Tesseract from 'tesseract.js';
import { useNavigate, useParams } from 'react-router-dom';
import { addCatalogueItemByText } from '../../hooks/catalogueItems';
import {
  CatalogueHero,
  CatalogueSectionCard,
  CatalogueShell,
  PrimaryCatalogueButton,
} from './components/catalogueUi';

export default function UploadPictureAI() {
  const toast = useToast();
  const navigate = useNavigate();
  const { orgId } = useParams();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [extractedText, setExtractedText] = useState('');

  const createFromText = async (text: string) => {
    if (!orgId) {
      return;
    }

    const response = await addCatalogueItemByText(orgId, text);

    if (!response.success) {
      toast({
        title: 'AI item creation failed',
        description: 'The text was extracted, but the item could not be created.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    toast({
      title: 'Product generated',
      description: 'The extracted text has been turned into a catalogue item.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    navigate(`/organization/${orgId}/catalogue`);
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setLoading(true);
    setProgress(0);
    setExtractedText('');

    try {
      const result = await Tesseract.recognize(file, 'eng', {
        logger: (message) => {
          if (message.status === 'recognizing text') {
            setProgress(Math.round(message.progress * 100));
          }
        },
      });

      setExtractedText(result.data.text);
      toast({
        title: 'Text extracted',
        description: 'Review the extracted details, then generate the catalogue item.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Extraction failed',
        description: 'Try a clearer image with readable product text.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <CatalogueShell compact>
      <CatalogueHero
        eyebrow="AI entry"
        title="Extract product details from a photo or screenshot"
        description="Use OCR to pull text from packaging, supplier screenshots, or product labels, then turn that into a catalogue item with minimal typing."
      />

      <Stack spacing={6}>
        <CatalogueSectionCard
          title="Upload source image"
          description="Choose a clear image with readable product information. This works best with screenshots and labels."
        >
          <VStack align="stretch" spacing={4}>
            <Input
              type="file"
              accept="image/*"
              onChange={handleUpload}
              borderRadius="16px"
              p={1.5}
              bg="gray.50"
            />
            {loading ? (
              <>
                <Progress value={progress} size="sm" borderRadius="full" />
                <Text fontSize="sm" color="gray.500">
                  Extracting text... {progress}%
                </Text>
              </>
            ) : (
              <Text fontSize="sm" color="gray.500">
                After extraction, you can review the text before generating the item.
              </Text>
            )}
          </VStack>
        </CatalogueSectionCard>

        <CatalogueSectionCard
          title="Extracted text"
          description="Review the output before pushing it into the catalogue."
        >
          <Textarea
            value={extractedText}
            onChange={(event) => setExtractedText(event.target.value)}
            minH={{ base: '220px', md: '300px' }}
            borderRadius="18px"
            bg="gray.50"
            placeholder="Extracted product details will appear here."
          />
        </CatalogueSectionCard>

        <VStack align="stretch" spacing={3}>
          <PrimaryCatalogueButton
            onClick={() => createFromText(extractedText)}
            isDisabled={!extractedText.trim() || loading}
          >
            Generate item from extracted text
          </PrimaryCatalogueButton>
          <Button borderRadius="16px" variant="ghost" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </VStack>
      </Stack>
    </CatalogueShell>
  );
}
